import { z } from "zod";
import type { CreateNewsRequest } from "../types/news.types";

export type NewsFormValues = CreateNewsRequest;

export interface NewsFormSubmitValues extends NewsFormValues {
  thumbnail_file: File | null;
}

/** Mirrors UpdateNewRequest/CreateNewRequest Title MaximumLength(255). */
export const MAX_NEWS_TITLE_LENGTH = 255;

/** Mirrors MaxThumbnailSizeBytes in CreateNewsHandler (5 MB). */
export const MAX_NEWS_THUMBNAIL_SIZE_BYTES = 5 * 1024 * 1024;

/** Mirrors AllowedThumbnailContentTypes in CreateNewsHandler. */
export const ACCEPTED_NEWS_THUMBNAIL_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
] as const;

export interface NewsFormValidationMessages {
  titleRequired: string;
  titleTooLong: string;
  contentRequired: string;
  thumbnailTooLarge: string;
  thumbnailInvalidType: string;
}

export const hasTextContent = (value: string): boolean =>
  value.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;

export type NewsThumbnailError = "too_large" | "invalid_type" | null;

export const getNewsThumbnailError = (file: File): NewsThumbnailError => {
  if (!ACCEPTED_NEWS_THUMBNAIL_TYPES.includes(file.type as (typeof ACCEPTED_NEWS_THUMBNAIL_TYPES)[number])) {
    return "invalid_type";
  }

  if (file.size > MAX_NEWS_THUMBNAIL_SIZE_BYTES) {
    return "too_large";
  }

  return null;
};

export const toNewsThumbnailErrorMessage = (
  error: Exclude<NewsThumbnailError, null>,
  messages: NewsFormValidationMessages,
): string => (error === "too_large" ? messages.thumbnailTooLarge : messages.thumbnailInvalidType);

/**
 * Shared by create and update: backend validators apply the same
 * Title/Content rules to both commands.
 */
export const createNewsFormSchema = (messages: NewsFormValidationMessages) =>
  z.object({
    title: z
      .string()
      .trim()
      .min(1, messages.titleRequired)
      .max(MAX_NEWS_TITLE_LENGTH, messages.titleTooLong),
    content: z.string().refine(hasTextContent, { message: messages.contentRequired }),
    location: z.string().optional().default(""),
    thumbnail_url: z.string().optional().default(""),
  });

export const updateNewsFormSchema = createNewsFormSchema;
